import { useAuth } from '@/lib/auth/auth';
import { APP_CONFIG } from '@/lib/app/config';
import { roundMapCoordinate } from '@/features/map/mapQuery';
import { locationStore } from '@/lib/location/location';
import { type PaginatedEventsResult } from '@/lib/api/apiTypes';
import { getEventLiveCutoffMs } from '@/features/events/eventFormatting';
import { getDeviceTimeZone } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';

const mapEventKeys = {
  all: ['map-events'] as const,
  list: (params: {
    latitude: number;
    longitude: number;
    originLatitude: number;
    originLongitude: number;
    timeWindow: 'map' | 'tonight' | 'live' | 'today';
    tag: string;
    sort: 'soonest' | 'closest';
    limit: number;
  }) =>
    [...mapEventKeys.all, params] as const,
};

export function useMapEvents({
  enabled = true,
  latitude,
  longitude,
  originLatitude,
  originLongitude,
  tag = '',
  timeWindow = 'map',
  sort = 'soonest',
  limit = APP_CONFIG.eventPageSize,
}: {
  enabled?: boolean;
  latitude?: number;
  longitude?: number;
  originLatitude?: number;
  originLongitude?: number;
  tag?: string;
  timeWindow?: 'map' | 'tonight' | 'live' | 'today';
  sort?: 'soonest' | 'closest';
  limit?: number;
} = {}) {
  const request = useAuth((state) => state.request);
  const coords = locationStore((state) => state.coords);
  const isReady = locationStore((state) => state.isReady);
  const queryLatitude = roundMapCoordinate(latitude ?? coords.latitude);
  const queryLongitude = roundMapCoordinate(longitude ?? coords.longitude);
  const distanceOriginLatitude = roundMapCoordinate(originLatitude ?? queryLatitude);
  const distanceOriginLongitude = roundMapCoordinate(originLongitude ?? queryLongitude);

  return useQuery({
    queryKey: mapEventKeys.list({
      latitude: queryLatitude,
      longitude: queryLongitude,
      originLatitude: distanceOriginLatitude,
      originLongitude: distanceOriginLongitude,
      timeWindow,
      tag,
      sort,
      limit,
    }),
    enabled: isReady && enabled,
    placeholderData: (previousData) => previousData,
    staleTime: 30_000,
    queryFn: async () => {
      const requestTimeWindow = timeWindow === 'live' ? 'map' : timeWindow;
      const requestLimit = timeWindow === 'live' ? Math.max(limit * 2, 40) : limit;
      const params = new URLSearchParams({
        limit: String(requestLimit),
        offset: '0',
        sort,
        lat: String(queryLatitude),
        lng: String(queryLongitude),
        origin_lat: String(distanceOriginLatitude),
        origin_lng: String(distanceOriginLongitude),
        tz: getDeviceTimeZone(),
      });
      if (requestTimeWindow === 'today') {
        params.set('day', 'today');
      } else {
        params.set('time_window', requestTimeWindow);
      }
      if (tag) {
        params.set('tag', tag);
      }

      const payload = await request<PaginatedEventsResult>(`/events?${params.toString()}`);
      if (timeWindow !== 'live') {
        return payload;
      }

      const now = Date.now();
      const liveHorizon = now + 2 * 60 * 60 * 1000;
      const filteredEvents = payload.events.filter((event) => {
        const startsAt = new Date(event.starts_at).getTime();
        const endsAt = getEventLiveCutoffMs(event.starts_at, event.ends_at);
        return endsAt > now && startsAt < liveHorizon;
      });
      return {
        ...payload,
        events: filteredEvents,
      };
    },
  });
}
